import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { postsApi } from "../api";
import { Reveal, NewsCard } from "../components/Shared";
import { NewsletterForm } from "../components/Footer";
import { ScrollProgress } from "../components/Effects";
import Seo from "../components/Seo";
import { sanitizeHtml } from "../lib/sanitize";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setPost(null);
    const load = async () => {
      try {
        const found = await postsApi.get(slug);
        setPost(found);
        const all = await postsApi.list();
        const rel = all
          .filter((p) => p.id !== found.id)
          .sort((a, b) => (b.category === found.category) - (a.category === found.category))
          .slice(0, 3);
        setRelated(rel);
      } catch {
        setNotFound(true);
      }
    };
    load();
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="post-not-found">
        <h1 className="font-serif-d text-4xl text-[#0f2438] mb-4">Article not found</h1>
        <Link to="/blog" className="gee-link text-[#0f2438]">
          <ArrowLeft size={16} /> Back to News
        </Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-screen" />;

  const dateStr = new Date(post.date).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt}
        image={post.image}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.date,
            dateModified: (post.updated_at || "").slice(0, 10) || post.date,
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: "Global Equine Group" },
            articleSection: post.category,
            keywords: (post.tags || []).join(", "),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "/" },
              { "@type": "ListItem", position: 2, name: "News", item: "/blog" },
              { "@type": "ListItem", position: 3, name: post.title },
            ],
          },
        ]}
      />
      <ScrollProgress />
      {/* Hero */}
      <section className="gee-hero pt-24" style={{ minHeight: "60vh" }}>
        <div className="gee-hero-bg" style={{ backgroundImage: `url(${post.image})`, opacity: 0.45 }} />
        <div className="gee-hero-overlay" />
        <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12 pt-20 pb-28">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#29abe2] text-sm mb-8 hover:text-white transition-colors" data-testid="back-to-news-link">
              <ArrowLeft size={15} /> Back to News
            </Link>
            <p className="gee-kicker mb-4">{post.category}</p>
            <h1 className="font-serif-d text-white text-3xl md:text-5xl leading-tight" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 mt-8 text-sm text-[#b9c9d6]">
              <span className="flex items-center gap-2"><Calendar size={14} className="text-[#29abe2]" /> {dateStr}</span>
              <span className="flex items-center gap-2"><User size={14} className="text-[#29abe2]" /> {post.author}</span>
            </div>
          </Reveal>
        </div>
        <div className="gee-hero-notch" />
      </section>

      {/* Body */}
      <section className="py-20 bg-white">
        <div className="max-w-[780px] mx-auto px-6 lg:px-12">
          <div
            className="gee-prose"
            data-testid="post-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
          />
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-[#e8eef4]">
              <Tag size={15} className="text-[#8aa0b0]" />
              {post.tags.map((t) => (
                <span key={t} className="bg-[#eaf5fc] text-[#1d8fc0] px-3 py-1 text-xs uppercase tracking-wider">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter inline */}
      <section className="bg-[#f7fbfd] py-16">
        <div className="max-w-[780px] mx-auto px-6 lg:px-12 text-center">
          <h3 className="font-serif-d text-[#0f2438] text-2xl md:text-3xl mb-3">Never miss a winner</h3>
          <p className="text-[#5b6d7d] mb-8">Subscribe for race reports and insights from the team.</p>
          <div className="flex justify-center">
            <NewsletterForm dark={false} id="post-newsletter" />
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <h2 className="font-serif-d text-[#0f2438] text-3xl mb-10">More News</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((p, i) => (
                <NewsCard key={p.id} post={p} delay={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
